import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { Priority } from '../../enums';
import { BaseEntity } from './base.entity';
import type { IssueActivityEntity } from './issue-activity.entity';
import { IssueFileEntity } from './issue-file.entity';
import { IssueLinkEntity } from './issue-link.entity';
import { IssueNoteEntity } from './issue-note.entity';
import { IssueStatusHistoryEntity } from './issue-status-history.entity';
import { IssueUserEntity } from './issue-user.entity';
import { UserEntity } from './user.entity';

@Index('IDX_issue_status', ['status'], {})
@Index('IDX_issue_statusUpdatedDatetime', ['statusUpdatedDatetime'], {})
@Index('IDX_issue_type', ['type'], {})
@Index('IDX_issue_priority', ['priority'], {})
@Index('IDX_issue_assignee', ['assigneeUserId'], {})
@Entity('issue')
export class IssueEntity extends BaseEntity {
	@Column('longtext', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('varchar', { name: 'status', length: 64, default: () => "'To-Do'" })
	public status!: string;

	@Column('datetime', { name: 'statusUpdatedDatetime', nullable: true })
	public statusUpdatedDatetime!: Date | null;

	@Column('varchar', { name: 'type', nullable: true, length: 64 })
	public type!: string | null;

	@Column('enum', {
		name: 'priority',
		enum: Priority,
		default: Priority.NORMAL,
	})
	public priority!: Priority;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	@Column('int', {
		name: '_fk_user__id_assignee',
		nullable: true,
		unsigned: true,
	})
	public assigneeUserId!: number | null;

	@ManyToOne(() => UserEntity, (user) => user.assignedIssues, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_user__id_assignee', referencedColumnName: 'id' }])
	public assignee!: UserEntity | null;

	@OneToMany(() => IssueNoteEntity, (issueNote) => issueNote.fkIssue, {})
	public issueNotes!: Array<IssueNoteEntity>;

	@OneToMany(() => IssueUserEntity, (issueUser) => issueUser.fkIssue, {})
	public issueUsers!: Array<IssueUserEntity>;

	@OneToMany(() => IssueStatusHistoryEntity, (history) => history.fkIssue, {})
	public statusHistory!: Array<IssueStatusHistoryEntity>;

	@OneToMany(() => IssueLinkEntity, (link) => link.fkIssue, {})
	public issueLinks!: Array<IssueLinkEntity>;

	@OneToMany(
		'IssueActivityEntity',
		(activity: IssueActivityEntity) => activity.fkIssue,
		{},
	)
	public activities!: Array<IssueActivityEntity>;

	@OneToMany(() => IssueFileEntity, (issueFile) => issueFile.fkIssue, {})
	public issueFiles!: Array<IssueFileEntity>;
}
